import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MailCheck, Send, Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { agencyUpdateAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * UpdateAgencyInfoLinkDialog — emails the agency's Principal a secure,
 * pre-filled link to review and update the agency's business details (NPN,
 * EIN, email, phone, address) and submit banking information for payouts.
 * The link always goes to the linked Principal agent's email.
 */
export const UpdateAgencyInfoLinkDialog = ({ open, onOpenChange, agency }) => {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => { if (open) setResult(null); }, [open]);

  const hasPrincipal = !!agency?.principal_agent_npn;

  const handleSend = async () => {
    if (!agency?.agency_id) return;
    setBusy(true);
    try {
      const res = await agencyUpdateAPI.createLink(agency.agency_id);
      setResult(res.data);
      toast.success(res.data?.email_sent
        ? 'Update link sent to the Principal'
        : 'Link created — email delivery pending, share the link below');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create update link'));
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = () => {
    if (!result?.update_link) return;
    navigator.clipboard.writeText(result.update_link);
    toast.success('Update link copied');
  };

  const handleClose = () => { onOpenChange(false); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent data-testid="update-agency-info-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MailCheck size={18} /> Update Agency Info
          </DialogTitle>
          <DialogDescription>
            Email the agency's Principal a secure, pre-filled link so they can review and
            complete the agency's details (NPN, EIN, email, phone, address) and submit
            banking information for payouts. This does not change any portal access.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="py-3 space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="text-slate-600 mb-1">A secure update link will be emailed to the Principal of:</div>
              <div className="font-semibold text-slate-900">{agency?.agency_name}</div>
              <div className="text-xs text-slate-600 mt-2">
                <span className="font-medium">Principal:</span>{' '}
                {agency?.principal_agent_name || (hasPrincipal ? `NPN ${agency.principal_agent_npn}` : <span className="text-red-600">(not linked)</span>)}
              </div>
            </div>
            {!hasPrincipal && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                This agency has no Principal linked. Edit the agency and link a Principal agent (with an email on file) before sending an update link.
              </div>
            )}
            <p className="text-[11px] text-slate-500">
              The link is single-use, expires in 7 days and any banking details are encrypted and sent for KR review.
            </p>
          </div>
        ) : (
          <div className="py-2 space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Update link ready.</div>
                <div className="text-xs mt-0.5">
                  {result.email_sent
                    ? 'The email is on its way to the Principal.'
                    : 'Email delivery pending — copy the link below and share it with the Principal.'}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Secure link</div>
              <div className="flex items-center gap-2">
                <div
                  className="font-mono text-xs text-slate-800 bg-slate-100 px-2 py-1 rounded border border-slate-200 truncate flex-1 min-w-0"
                  data-testid="update-agency-info-link-url"
                >
                  {result.update_link}
                </div>
                <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-agency-update-link-btn">
                  <Copy size={14} />
                </Button>
              </div>
              <div className="text-[11px] text-slate-500">Sent to: {result.email}</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={handleClose} data-testid="update-agency-info-cancel-btn">Cancel</Button>
              <Button onClick={handleSend} disabled={busy || !hasPrincipal} data-testid="send-agency-update-link-btn">
                <Send size={14} className="mr-1.5" /> {busy ? 'Sending…' : 'Send Update Link'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} data-testid="update-agency-info-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateAgencyInfoLinkDialog;
